import { Fragment } from "react";
import { SpinnerCircular } from "spinners-react";
import { Dialog } from "../../components/CommonComponents/Dialog/Dialog";
import { ShowField } from "../../components/ViewApplication/ViewApplication";
import {
  TApplication,
  useApplicationFormSteps,
} from "../../hooks/applications.hooks";
import { sequenceReviewFields } from "../../components/FieldSequence/fieldSequenceOrder";
import { isListedOnDetailScreen } from "../../utils/detailScreenFields";
/**
 * Not imported while detail screens leave the Admin's empty spaces out
 * (they only apply to the applicant form); this was the blank row drawn
 * for each one:
 * import { ReviewFieldSpacer } from "../../components/FieldSequence/ReviewFieldSpacer";
 */
import { useApplicantFormFieldSequence } from "../../hooks/field-sequence.hooks";

export function RenderApplication({
  application,
  onClose,
}: {
  application: TApplication & Record<string, unknown>;
  onClose: () => unknown;
}) {
  const {
    data: result,
    isError,
    isFetching,
  } = useApplicationFormSteps(Number(application?.name_of_service));

  const steps = result?.data || [];

  /**
   * The Admin's Field Sequence, or null while its applicant form switch is
   * off. This preview shows the applicant what they just edited in the
   * applicant form, so it follows the same switch as that form.
   */
  const { data: applicantFieldOrder } = useApplicantFormFieldSequence();
  let i = 0;
  let toRender = (
    <>
      {(steps || [])
        .filter((e) => !["Documents", "NOC"].includes(e.type))
        .map((e) => (
          <Fragment key={`step-${e.id}`}>
            {/**
               * Each step keeps its place and lists its own fields in the
               * Admin's order, without the Admin's empty spaces (they only
               * apply to the applicant form); with the switch off they stay
               * in the step's default order.
               *
               * BEFORE - the step's default order:
               * {e.fields?.map((e) => (
               *   <ShowField
               *     field={e}
               *     index={++i}
               *     key={`field-${e.id}`}
               *     data={application || {}}
               *   />
               * ))}
               */}
            {sequenceReviewFields(
              (e.fields || [])
                /** Main Khasara No. and empty Inactive fields are left out (see detailScreenFields.ts). */
                .filter((field) => isListedOnDetailScreen(field, application))
                .map((field) => ({ ...field, id: Number(field.id) })),
              applicantFieldOrder
            ).map((field) => (
              <ShowField
                field={field}
                index={++i}
                key={`field-${field.id}`}
                data={application || {}}
              />
            ))}
          </Fragment>
        ))}
    </>
  );

  if (isFetching || isError) {
    toRender = (
      <div className="flex justify-center items-center p-8">
        {isError ? (
          <p>Something went wrong while fetching application info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  }
  return (
    <Dialog onClose={onClose} open className="md:w-4/5 lg:w-3/5 md:h-4/5">
      <div className="p-4 md:p-8 h-full flex overflow-auto flex-col">
        <div className="mb-8 flex justify-center items-center">
          <h4 className="m-0 grow text-center text-2xl font-bold">
            {"Edit Application Preview"}
          </h4>
          <button className="text-xl" onClick={onClose}>
            <i className="fa fa-close"></i>
          </button>
        </div>
        <div className="w-full grow overflow-auto">{toRender}</div>
      </div>
    </Dialog>
  );
}
